import { inject, Injectable } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import {
  CONFIGURATION_SETTING_KEYS,
  ConfigurationId,
  ESettingInputType,
  IConfiguration,
} from '@web/dtos';
import {
  ConfigurationControlValue,
  IConfigurationForm,
  IConfigurationFormData,
  IConfigurationFormDataWrapper,
  IConfigurationFormWrapper,
} from '../../entities/configuration-entity';
import { Factory } from '../factory';

@Injectable({
  providedIn: 'root',
})
export class ConfigurationFormsFactoryService extends Factory<
  IConfigurationFormWrapper,
  IConfiguration[]
> {
  private readonly formBuilder: FormBuilder = inject(FormBuilder);

  buildForm(
    configuration: IConfiguration[]
  ): FormGroup<IConfigurationFormWrapper> {
    return this.formBuilder.group<IConfigurationFormWrapper>({
      configurations: this.formBuilder.array<FormGroup<IConfigurationForm>>(
        configuration.map(
          (setting: IConfiguration): FormGroup<IConfigurationForm> =>
            this.buildConfigurationFormGroup(setting)
        )
      ),
    });
  }

  buildConfigurationFormGroup(
    setting: IConfiguration
  ): FormGroup<IConfigurationForm> {
    return this.formBuilder.group<IConfigurationForm>({
      id: this.formBuilder.control<ConfigurationId | null>(
        setting.id ?? null
      ),
      settingKey: this.formBuilder.nonNullable.control(setting.settingKey),
      label: this.formBuilder.nonNullable.control(setting.label),
      channels: this.formBuilder.nonNullable.control([...setting.channels]),
      configurationGroup: this.formBuilder.control(setting.configurationGroup),
      description: this.formBuilder.control(setting.description),
      inputType: this.formBuilder.control(setting.inputType),
      requiredLicense: this.formBuilder.nonNullable.control({
        ...setting.requiredLicense,
      }),
      value: this.formBuilder.control<ConfigurationControlValue>(
        this.parseValue(setting)
      ),
      position: this.formBuilder.nonNullable.control(setting.position),
      dependsOn: this.formBuilder.nonNullable.control([
        ...(setting.dependsOn ?? []),
      ]),
      dependents: this.formBuilder.nonNullable.control([
        ...(setting.dependents ?? []),
      ]),
      isDisabled: this.formBuilder.nonNullable.control<boolean | undefined>(
        setting.isDisabled
      ),
      link: this.formBuilder.nonNullable.control<string | undefined>(
        setting.link
      ),
    });
  }

  buildEntity(form: FormGroup<IConfigurationFormWrapper>): IConfiguration[] {
    const wrapper: IConfigurationFormDataWrapper =
      form.getRawValue() as IConfigurationFormDataWrapper;

    return wrapper.configurations.map(
      (setting: IConfigurationFormData): IConfiguration => {
        const { id, value, ...configuration } = setting;

        return {
          ...configuration,
          ...(id === null ? {} : { id }),
          value: this.serializeValue(
            setting.settingKey,
            setting.inputType,
            value
          ),
        };
      }
    );
  }

  controlKey(setting: IConfiguration): string {
    return (
      setting.id ??
      `${setting.settingKey}-${[...setting.channels].sort().join('-')}`
    );
  }

  private parseValue(setting: IConfiguration): ConfigurationControlValue {
    switch (setting.inputType) {
      case ESettingInputType.Checkbox:
        return setting.value === 'true';
      case ESettingInputType.NumberField:
        return setting.value === '' ? null : Number(setting.value);
      case ESettingInputType.Custom:
        return this.parseCustomValue(setting);
      case ESettingInputType.MultiSelect:
        return this.parseMultiSelect(setting.value);
      default:
        return setting.value;
    }
  }

  private parseCustomValue(
    setting: IConfiguration
  ): ConfigurationControlValue {
    if (
      setting.settingKey === CONFIGURATION_SETTING_KEYS.ServiceAreaId ||
      setting.settingKey === CONFIGURATION_SETTING_KEYS.SubscriptionServiceArea
    ) {
      return setting.value === '' ? null : Number(setting.value);
    }

    if (
      setting.settingKey ===
      CONFIGURATION_SETTING_KEYS.SalutationsRegistration
    ) {
      return [];
    }

    if (
      setting.settingKey === CONFIGURATION_SETTING_KEYS.UicTariffAreas ||
      setting.settingKey === CONFIGURATION_SETTING_KEYS.VdvKaTariffAreas
    ) {
      return this.parseTariffAreaIds(setting.value);
    }

    return setting.value;
  }

  private parseMultiSelect(value: string): string[] {
    if (value === '') {
      return [];
    }

    try {
      const parsed: unknown = JSON.parse(value);
      return Array.isArray(parsed) &&
        parsed.every((item: unknown) => typeof item === 'string')
        ? parsed
        : [];
    } catch {
      return [];
    }
  }

  private parseTariffAreaIds(value: string): number[] {
    try {
      const parsed: unknown = JSON.parse(value);
      if (!Array.isArray(parsed)) {
        return [];
      }

      return parsed
        .map((item: unknown): number => Number(item))
        .filter((item: number): boolean => Number.isFinite(item));
    } catch {
      return [];
    }
  }

  private serializeValue(
    settingKey: string,
    inputType: ESettingInputType | null,
    value: ConfigurationControlValue
  ): string {
    if (settingKey === CONFIGURATION_SETTING_KEYS.SalutationsRegistration) {
      return '[]';
    }

    if (inputType === ESettingInputType.MultiSelect) {
      return JSON.stringify(Array.isArray(value) ? value : []);
    }

    if (inputType === ESettingInputType.Custom && Array.isArray(value)) {
      return JSON.stringify(value ?? []);
    }

    return value?.toString() ?? '';
  }
}
